import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Scale, X, MessageSquare, Bookmark, Share2, Info, ArrowLeft, Check, Trophy, Globe, BadgeCheck } from 'lucide-react';
import { toast } from 'sonner';
import { api, mediaUrl } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { listingPath } from '@/lib/listingUrl';
import { useCompare } from '@/lib/compareStore';
import { compareRowsFor, bestIndexFor } from '@/lib/compareSpecs';
import { CompareGuestNudge } from '@/components/marketplace/CompareCheckbox';
import SEOHead from '@/components/marketplace/SEOHead';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';

const TYPE_LABEL = { sell: 'for sale', wanted: 'wanted', rent: 'for rent', service: 'services' };
const STATE_NOTE = {
  sold: 'Sold — no longer accepting inquiries',
  expired: 'Expired — no longer accepting inquiries',
  unavailable: 'Not available',
  removed: 'Listing no longer available',
};

const ColumnThumb = ({ item, thumb }) => {
  const [err, setErr] = useState(false);
  if (!thumb || err) {
    return <div className='flex h-full w-full items-center justify-center text-[9px] text-muted-foreground'>No image</div>;
  }
  return (
    <img
      src={mediaUrl(thumb.thumb_url || thumb.public_url)}
      alt={item.title}
      className='h-full w-full object-cover'
      onError={() => setErr(true)}
    />
  );
};

const ColumnHead = ({ item, onRemove }) => {
  const unavailable = item.availability_state && item.availability_state !== 'live';
  const thumb = (item.media || []).find((m) => (m.kind || 'image') === 'image');
  const price = item.asking_price ?? item.price_amount ?? item.budget_amount ?? item.starting_price_inr ?? null;
  const country = (item.country || '').trim();
  const isIntl = !!country && country.toLowerCase() !== 'india';
  return (
    <th
      className='min-w-[220px] border-b border-l bg-card p-3 align-top text-left'
      data-testid={`compare-column-${item.id}`}
    >
      <div className='flex items-start justify-between gap-2'>
        <div className='h-16 w-16 shrink-0 overflow-hidden rounded-md border bg-muted'>
          <ColumnThumb item={item} thumb={thumb} />
        </div>
        {onRemove && (
          <button
            type='button'
            onClick={() => onRemove(item.id)}
            className='rounded p-1 text-muted-foreground hover:text-destructive'
            aria-label={`Remove ${item.title} from comparison`}
            data-testid={`compare-remove-${item.id}`}
          >
            <X className='h-4 w-4' />
          </button>
        )}
      </div>
      <div className='mt-2 space-y-1'>
        {item.availability_state === 'removed' ? (
          <div className='text-sm font-semibold text-muted-foreground'>{item.title}</div>
        ) : (
          <Link
            to={listingPath(item)}
            className='line-clamp-2 text-sm font-semibold leading-snug hover:underline underline-offset-4'
            data-testid={`compare-title-${item.id}`}
          >
            {item.title}
          </Link>
        )}
        <div className='text-sm font-semibold tabular-nums'>
          {price != null ? `₹ ${Number(price).toLocaleString('en-IN')}` : 'Price on Request'}
        </div>
        <div className='flex flex-wrap gap-1'>
          {item.is_gst_verified && (
            <span className='inline-flex items-center gap-1 rounded-full bg-[hsl(var(--verified))]/10 px-1.5 py-0.5 text-[10px] font-medium text-[hsl(var(--verified))]'>
              <BadgeCheck className='h-3 w-3' /> GST Verified
            </span>
          )}
          {isIntl && (
            <span
              className='inline-flex items-center gap-1 rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-800'
              data-testid={`compare-intl-badge-${item.id}`}
              title={`International · Located in ${country}`}
            >
              <Globe className='h-3 w-3' /> Intl
            </span>
          )}
        </div>
        {unavailable && (
          <div
            className='rounded bg-secondary px-1.5 py-0.5 text-[10px] font-semibold uppercase text-muted-foreground'
            data-testid={`compare-state-${item.id}`}
          >
            {STATE_NOTE[item.availability_state]}
          </div>
        )}
      </div>
    </th>
  );
};

const ComparePage = ({ sharedMode = false }) => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const compare = useCompare();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sharedName, setSharedName] = useState('');
  const [droppedCount, setDroppedCount] = useState(0);
  const [onlyDifferences, setOnlyDifferences] = useState(false);

  const [rfqOpen, setRfqOpen] = useState(false);
  const [rfqSending, setRfqSending] = useState(false);
  const [rfq, setRfq] = useState({ name: '', company: '', email: '', mobile: '', message: '' });

  const [saveOpen, setSaveOpen] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [saving, setSaving] = useState(false);
  const [shareUrl, setShareUrl] = useState('');

  const ids = compare.items.map((i) => i.id).join(',');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (sharedMode) {
        const { data } = await api.get(`/compare/shared/${token}`);
        setItems(data.items || []);
        setSharedName(data.name || 'Shared comparison');
      } else if (!ids) {
        setItems([]);
      } else {
        const { data } = await api.get('/compare/listings', { params: { ids } });
        setItems(data.items || []);
        setDroppedCount((data.dropped || []).length);
      }
    } catch (e) {
      if (sharedMode) setItems(null);
      else toast.error('Could not load the comparison');
    } finally { setLoading(false); }
  }, [ids, sharedMode, token]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!user) return;
    setRfq((r) => ({
      ...r,
      name: r.name || user.name || '',
      company: r.company || user.company_name || '',
      email: r.email || user.email || '',
      mobile: r.mobile || user.mobile || '',
    }));
  }, [user]);

  const listingType = items.find((i) => i.listing_type)?.listing_type || compare.listingType;
  const rows = useMemo(() => compareRowsFor(listingType), [listingType]);

  const liveItems = items.filter((i) => i.availability_state === 'live');
  const categoryIds = new Set(items.map((i) => i.category_id).filter(Boolean));
  const crossCategory = categoryIds.size > 1;

  const rowData = useMemo(() => rows.map((row) => {
    const values = items.map((i) => (i.availability_state === 'removed' ? null : (row.get(i) ?? null)));
    const present = values.filter((v) => v !== null && v !== '');
    const identical = present.length === values.length && new Set(present.map(String)).size <= 1;
    return { row, values, identical, best: bestIndexFor(row, items), empty: present.length === 0 };
  }), [rows, items]);

  const visibleRows = rowData.filter((r) => !r.empty && (!onlyDifferences || !r.identical));
  const identicalCount = rowData.filter((r) => !r.empty && r.identical).length;

  const submitRfq = async () => {
    if (!user) return navigate('/login', { state: { from: '/compare' } });
    if (!rfq.name.trim() || !rfq.email.trim() || !rfq.mobile.trim()) {
      return toast.error('Name, email and mobile are required');
    }
    if ((rfq.message || '').trim().length < 5) return toast.error('Please describe your requirement');
    setRfqSending(true);
    try {
      const { data } = await api.post('/compare/rfq', {
        listing_ids: liveItems.map((i) => i.id),
        ...rfq,
      });
      toast.success(`Quote request sent to ${data.sent_count} seller${data.sent_count > 1 ? 's' : ''}`);
      if ((data.skipped || []).length) {
        toast.info(`${data.skipped.length} listing(s) were skipped: ${data.skipped[0].reason}`);
      }
      setRfqOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not send the quote request');
    } finally { setRfqSending(false); }
  };

  const submitSave = async () => {
    if (!saveName.trim()) return toast.error('Give the comparison a name');
    setSaving(true);
    try {
      const { data } = await api.post('/compare/saved', {
        name: saveName.trim(),
        listing_ids: items.map((i) => i.id),
      });
      const url = `${window.location.origin}/compare/s/${data.share_token}`;
      setShareUrl(url);
      toast.success('Comparison saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save the comparison');
    } finally { setSaving(false); }
  };

  if (sharedMode && items === null) {
    return (
      <div className='indus-container py-16 text-center' data-testid='compare-shared-not-found'>
        <SEOHead title='Comparison not found | IndusFact' robots='noindex,nofollow' />
        <h1 className='text-2xl font-semibold'>This comparison link is no longer valid</h1>
        <Link to='/sell' className='mt-4 inline-block text-primary underline'>Browse listings</Link>
      </div>
    );
  }

  return (
    <div className='indus-container py-6' data-testid='compare-page'>
      {/* A comparison is a private working view, not content — never indexed. */}
      <SEOHead
        title={sharedMode ? `${sharedName} | Comparison | IndusFact` : 'Compare listings | IndusFact'}
        description='Compare industrial listings side by side on IndusFact.'
        robots='noindex,nofollow'
      />

      <div className='flex flex-wrap items-start justify-between gap-3'>
        <div>
          <h1 className='flex items-center gap-2 text-2xl font-bold tracking-tight sm:text-3xl'>
            <Scale className='h-6 w-6 text-primary' />
            {sharedMode ? sharedName : 'Compare listings'}
          </h1>
          <p className='mt-1 text-sm text-muted-foreground'>
            {items.length > 0
              ? `${items.length} ${TYPE_LABEL[listingType] || ''} listing${items.length > 1 ? 's' : ''} side by side`
              : 'Pick listings from any browse page to compare them here.'}
          </p>
        </div>
        <div className='flex flex-wrap items-center gap-2'>
          {!sharedMode && (
            <Button variant='outline' size='sm' onClick={() => navigate(-1)} data-testid='compare-back-btn'>
              <ArrowLeft className='mr-1.5 h-4 w-4' /> Keep browsing
            </Button>
          )}
          {liveItems.length > 0 && (
            <Button size='sm' onClick={() => (user ? setRfqOpen(true) : navigate('/login', { state: { from: '/compare' } }))} data-testid='compare-rfq-btn'>
              <MessageSquare className='mr-1.5 h-4 w-4' /> Request quote from all ({liveItems.length})
            </Button>
          )}
          {!sharedMode && items.length > 0 && (
            <Button
              variant='outline'
              size='sm'
              onClick={() => { setShareUrl(''); setSaveOpen(true); }}
              data-testid='compare-save-btn'
            >
              <Bookmark className='mr-1.5 h-4 w-4' /> Save comparison
            </Button>
          )}
        </div>
      </div>

      {!sharedMode && !compare.isLoggedIn && items.length > 0 && (
        <div className='mt-4'>
          <CompareGuestNudge guestMax={compare.guestMax} userMax={compare.userMax} />
        </div>
      )}

      {droppedCount > 0 && (
        <div
          className='mt-4 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900'
          data-testid='compare-dropped-note'
        >
          <Info className='mt-0.5 h-4 w-4 shrink-0' />
          <span>
            {droppedCount} listing{droppedCount > 1 ? 's were' : ' was'} left out — you can compare{' '}
            {compare.maxColumns} at a time{compare.isLoggedIn ? '' : ' as a guest'}.
          </span>
        </div>
      )}

      {crossCategory && (
        <div
          className='mt-4 flex items-start gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-900'
          data-testid='compare-cross-category-note'
        >
          <Info className='mt-0.5 h-4 w-4 shrink-0' />
          <span>
            These listings sit in different categories, so they aren&apos;t directly comparable — some specifications
            will only apply to one of them.
          </span>
        </div>
      )}

      {loading ? (
        <div className='py-16 text-center text-muted-foreground'>Loading comparison…</div>
      ) : items.length === 0 ? (
        <div className='mt-10 rounded-xl border border-dashed p-10 text-center' data-testid='compare-empty'>
          <Scale className='mx-auto h-10 w-10 text-muted-foreground' />
          <h2 className='mt-3 text-base font-semibold'>Nothing shortlisted yet</h2>
          <p className='mx-auto mt-1 max-w-md text-sm text-muted-foreground'>
            Tick <strong>Compare</strong> on any listing card, or use &quot;Add to compare&quot; on a listing page.
            Guests compare {compare.guestMax}; signed-in buyers compare {compare.userMax}, as often as they like.
          </p>
          <div className='mt-4 flex flex-wrap justify-center gap-2'>
            <Button asChild size='sm'><Link to='/sell'>Browse machines for sale</Link></Button>
            <Button asChild size='sm' variant='outline'><Link to='/rent'>Browse rentals</Link></Button>
          </div>
        </div>
      ) : (
        <>
          <div className='mt-5 flex items-center justify-between gap-3'>
            <label className='flex items-center gap-2 text-sm'>
              <Switch
                checked={onlyDifferences}
                onCheckedChange={setOnlyDifferences}
                data-testid='compare-only-differences-toggle'
              />
              <span>Show only differences</span>
              {identicalCount > 0 && (
                <span className='text-xs text-muted-foreground' data-testid='compare-identical-count'>
                  ({identicalCount} identical row{identicalCount > 1 ? 's' : ''})
                </span>
              )}
            </label>
          </div>

          <div className='mt-3 overflow-x-auto rounded-xl border' data-testid='compare-table-wrapper'>
            <table className='w-full border-collapse text-sm' data-testid='compare-table'>
              <thead>
                <tr>
                  <th className='sticky left-0 z-10 w-[180px] min-w-[160px] border-b bg-card p-3 text-left align-bottom'>
                    <span className='text-xs font-semibold uppercase tracking-wider text-muted-foreground'>
                      Specification
                    </span>
                  </th>
                  {items.map((item) => (
                    <ColumnHead
                      key={item.id}
                      item={item}
                      onRemove={sharedMode ? null : (id) => compare.remove(id)}
                    />
                  ))}
                </tr>
              </thead>
              <tbody>
                {visibleRows.map(({ row, values, identical, best }) => (
                  <tr key={row.key} className='even:bg-secondary/30' data-testid={`compare-row-${row.key}`}>
                    <th
                      scope='row'
                      className='sticky left-0 z-10 border-b bg-card p-3 text-left align-top text-xs font-medium text-muted-foreground'
                    >
                      {row.label}
                    </th>
                    {values.map((v, idx) => (
                      <td
                        key={`${row.key}-${idx}`}
                        className={`border-b border-l p-3 align-top ${
                          identical ? 'text-muted-foreground' : 'font-medium text-foreground'
                        }`}
                        data-testid={`compare-cell-${row.key}-${idx}`}
                      >
                        <span className='inline-flex items-center gap-1.5'>
                          {v === null || v === '' ? <span className='text-muted-foreground'>—</span> : v}
                          {best === idx && (
                            <span
                              className='inline-flex items-center gap-0.5 rounded-full bg-emerald-100 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-800'
                              data-testid={`compare-best-${row.key}`}
                              title='Best value in this row'
                            >
                              <Trophy className='h-3 w-3' /> Best
                            </span>
                          )}
                        </span>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {onlyDifferences && visibleRows.length === 0 && (
            <div className='mt-3 rounded-lg border bg-secondary/40 p-4 text-sm text-muted-foreground' data-testid='compare-no-differences'>
              These listings are identical on every specification they both provide.
            </div>
          )}
        </>
      )}

      {/* Shortlist RFQ — one message, one set of contact details, one inquiry per seller */}
      <Dialog open={rfqOpen} onOpenChange={setRfqOpen}>
        <DialogContent data-testid='compare-rfq-dialog'>
          <DialogHeader>
            <DialogTitle>Request a quote from all {liveItems.length} sellers</DialogTitle>
            <DialogDescription>
              Each seller receives this as a normal inquiry on their own listing. They never see the other
              listings in your comparison.
            </DialogDescription>
          </DialogHeader>
          <div className='space-y-3'>
            <div className='grid grid-cols-2 gap-3'>
              <div>
                <Label>Your name</Label>
                <Input value={rfq.name} onChange={(e) => setRfq({ ...rfq, name: e.target.value })} data-testid='compare-rfq-name' />
              </div>
              <div>
                <Label>Company</Label>
                <Input value={rfq.company} onChange={(e) => setRfq({ ...rfq, company: e.target.value })} data-testid='compare-rfq-company' />
              </div>
            </div>
            <div className='grid grid-cols-2 gap-3'>
              <div>
                <Label>Email</Label>
                <Input value={rfq.email} onChange={(e) => setRfq({ ...rfq, email: e.target.value })} data-testid='compare-rfq-email' />
              </div>
              <div>
                <Label>Mobile</Label>
                <Input value={rfq.mobile} onChange={(e) => setRfq({ ...rfq, mobile: e.target.value })} data-testid='compare-rfq-mobile' />
              </div>
            </div>
            <div>
              <Label>Your requirement</Label>
              <Textarea
                rows={5}
                value={rfq.message}
                onChange={(e) => setRfq({ ...rfq, message: e.target.value })}
                placeholder='Quantity, delivery location, timeline and anything else the sellers should quote against.'
                data-testid='compare-rfq-message'
              />
            </div>
            <div className='rounded-md bg-secondary/50 p-2.5 text-xs text-muted-foreground'>
              Going to: {liveItems.map((i) => i.title).join(' · ')}
            </div>
          </div>
          <DialogFooter>
            <Button variant='outline' onClick={() => setRfqOpen(false)}>Cancel</Button>
            <Button onClick={submitRfq} disabled={rfqSending} data-testid='compare-rfq-submit'>
              <MessageSquare className='mr-1.5 h-4 w-4' />
              {rfqSending ? 'Sending…' : `Send to ${liveItems.length} seller${liveItems.length > 1 ? 's' : ''}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Save + share (paid) */}
      <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
        <DialogContent data-testid='compare-save-dialog'>
          <DialogHeader>
            <DialogTitle>Save this comparison</DialogTitle>
            <DialogDescription>
              Keep the table beyond this session and share it by link with a colleague or approving manager.
            </DialogDescription>
          </DialogHeader>

          {!compare.canSave ? (
            <div className='space-y-3' data-testid='compare-save-upgrade-nudge'>
              <div className='rounded-lg border border-primary/30 bg-primary/5 p-4 text-sm'>
                <div className='font-semibold'>Saving a comparison is a paid feature</div>
                <ul className='mt-2 space-y-1 text-muted-foreground'>
                  <li className='flex gap-2'><Check className='mt-0.5 h-4 w-4 text-primary shrink-0' /> Name a comparison and reopen it any time from your dashboard</li>
                  <li className='flex gap-2'><Check className='mt-0.5 h-4 w-4 text-primary shrink-0' /> Share a link so your team sees the identical table</li>
                  <li className='flex gap-2'><Check className='mt-0.5 h-4 w-4 text-primary shrink-0' /> Comparing and sending shortlist requests stay free, always</li>
                </ul>
              </div>
              <DialogFooter>
                <Button variant='outline' onClick={() => setSaveOpen(false)}>Not now</Button>
                <Button asChild data-testid='compare-save-upgrade-cta'>
                  <Link to='/pricing'>See plans</Link>
                </Button>
              </DialogFooter>
            </div>
          ) : shareUrl ? (
            <div className='space-y-3' data-testid='compare-share-ready'>
              <Label>Share link</Label>
              <div className='flex gap-2'>
                <Input readOnly value={shareUrl} data-testid='compare-share-url' />
                <Button
                  variant='outline'
                  onClick={() => { navigator.clipboard?.writeText(shareUrl); toast.success('Link copied'); }}
                  data-testid='compare-share-copy'
                >
                  <Share2 className='h-4 w-4' />
                </Button>
              </div>
              <DialogFooter>
                <Button variant='outline' onClick={() => setSaveOpen(false)}>Close</Button>
                <Button asChild><Link to='/dashboard/comparisons'>View saved comparisons</Link></Button>
              </DialogFooter>
            </div>
          ) : (
            <div className='space-y-3'>
              <div>
                <Label>Name</Label>
                <Input
                  value={saveName}
                  onChange={(e) => setSaveName(e.target.value)}
                  placeholder='e.g. Press shop — Q3 shortlist'
                  data-testid='compare-save-name'
                />
              </div>
              <DialogFooter>
                <Button variant='outline' onClick={() => setSaveOpen(false)}>Cancel</Button>
                <Button onClick={submitSave} disabled={saving} data-testid='compare-save-submit'>
                  {saving ? 'Saving…' : 'Save comparison'}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ComparePage;
